import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "@/translations/en.json";
import fr from "@/translations/fr.json";
import es from "@/translations/es.json";

type Language = "en" | "fr" | "es";
type Dict = Record<string, any>;

interface PortalLanguageContextType {
  language: Language;
  /**
   * @param opts.persist  Defaults to true. Pass false when the language is
   *   derived from the URL rather than chosen by the user, so it doesn't
   *   clobber the stored portal preference.
   */
  setLanguage: (lang: Language, opts?: { persist?: boolean }) => void;
  t: (key: string, fallback?: string) => string;
}

const STORAGE_KEY = "portalLanguage";

/** Authenticated surfaces where the *stored* preference wins over the URL. */
const PREFERENCE_PREFIXES = ["/portal", "/admin", "/manufacturer"];

/**
 * Locale implied by a public marketing URL, or null for authenticated surfaces.
 *
 * On the public site each locale is a distinct indexable URL (/, /fr, /es) with
 * its own canonical + hreflang, so the path — not localStorage or
 * navigator.language — has to be authoritative. Without this, a visitor whose
 * browser reports fr-FR (or who ever visited /fr, since the preference is
 * sticky) would get French body copy served under the English URL, while the
 * <title> stayed English because it is derived from the path via tLang(). That
 * mismatch also got baked into the prerendered HTML at build time.
 *
 * /portal, /admin and /manufacturer return null: there the language is a real
 * user preference and must keep coming from localStorage.
 */
export function localeFromPublicPath(path: string): Language | null {
  if (PREFERENCE_PREFIXES.some((p) => path === p || path.startsWith(p + "/"))) {
    return null;
  }
  if (path === "/fr" || path.startsWith("/fr/")) return "fr";
  if (path === "/es" || path.startsWith("/es/")) return "es";
  return "en";
}

function getInitialLang(): Language {
  if (typeof window !== "undefined") {
    const fromPath = localeFromPublicPath(window.location.pathname);
    if (fromPath) return fromPath;

    const saved = window.localStorage.getItem(STORAGE_KEY) as Language | null;
    if (saved === "en" || saved === "fr" || saved === "es") return saved;
    const nav = navigator?.language?.toLowerCase() || "";
    if (nav.startsWith("fr")) return "fr";
    if (nav.startsWith("es")) return "es";
  }
  return "en";
}

function deepMerge<T extends object, U extends object>(target: T, source: U): T & U {
  const out: any = Array.isArray(target) ? [...(target as any)] : { ...(target as any) };
  for (const [k, v] of Object.entries(source as any)) {
    if (v && typeof v === "object" && !Array.isArray(v)) out[k] = deepMerge(out[k] ?? {}, v);
    else out[k] = v;
  }
  return out;
}

function lookup(key: string, dict: Dict): string | undefined {
  return key.split(".").reduce<any>((acc, part) => (acc == null ? undefined : acc[part]), dict);
}

// petites compléments inline (facultatifs)
const inlineEn: Dict = {
  portal: { dashboard: "Dashboard", products: "Products", locations: "Locations", metrics: "Metrics", assets: "Assets", settings: "Settings", support: "Support" },
  onboarding: { title: "Welcome to Fyrris", subtitle: "Complete your profile", step1: "Company Profile", step2: "First Location", step3: "Upload Logo", companyName: "Company Name", website: "Website", description: "Description", next: "Next", back: "Back", finish: "Finish", save: "Save Progress" }
};
const inlineFr: Dict = {
  portal: { dashboard: "Tableau de bord", products: "Produits", locations: "Emplacements", metrics: "Indicateurs", assets: "Ressources", settings: "Paramètres", support: "Support" },
  onboarding: { title: "Bienvenue chez Fyrris", subtitle: "Complétez votre profil", step1: "Profil de l'entreprise", step2: "Premier emplacement", step3: "Télécharger le logo", companyName: "Nom de l'entreprise", website: "Site web", description: "Description", next: "Suivant", back: "Retour", finish: "Terminer", save: "Sauvegarder" }
};
const inlineEs: Dict = {
  portal: { dashboard: "Panel", products: "Productos", locations: "Ubicaciones", metrics: "Métricas", assets: "Recursos", settings: "Configuración", support: "Soporte" },
  onboarding: { title: "Bienvenido a Fyrris", subtitle: "Completa tu perfil", step1: "Perfil de empresa", step2: "Primera ubicación", step3: "Subir logo", companyName: "Nombre de la empresa", website: "Sitio web", description: "Descripción", next: "Siguiente", back: "Atrás", finish: "Finalizar", save: "Guardar progreso" }
};

const resources: Record<Language, Dict> = {
  en: deepMerge(en as Dict, inlineEn),
  fr: deepMerge(fr as Dict, inlineFr),
  es: deepMerge(es as Dict, inlineEs)
};

/**
 * URL-locale–aware translation lookup.
 * Use this inside Helmet blocks where the active locale must come from the
 * URL path (/fr/*, /es/*) rather than from the user's stored preference.
 *
 * @example
 *   const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";
 *   <title>{tLang(locale, "home.meta.title", "Fyrris")}</title>
 */
export function tLang(lang: Language | string, key: string, fallback?: string): string {
  const safeLang = (lang === "fr" || lang === "es") ? lang : "en";
  const dict = resources[safeLang] ?? resources.en;
  // Fall back to English before falling back to the key itself. Without this,
  // a key missing from a locale renders the KEY as page content — the About
  // pages were shipping <meta name="description" content="about.meta.description">
  // in all three languages because `about.meta` existed in none of them.
  // English copy on a French page is a translation gap; a raw key is a defect.
  const v = lookup(key, dict) ?? lookup(key, resources.en);
  return v != null && typeof v === "string" ? v : (fallback ?? key);
}

const PortalLanguageContext = createContext<PortalLanguageContextType | null>(null);

export const PortalLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLang());
  // Language derived from the URL is display state, not a user choice, so it
  // must not overwrite the preference the user picked inside the portal.
  const [persist, setPersist] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (persist) window.localStorage.setItem(STORAGE_KEY, language);
      document.documentElement.setAttribute("lang", language);
    }
  }, [language, persist]);

  const setLanguage: PortalLanguageContextType["setLanguage"] = (lang, opts) => {
    setPersist(opts?.persist !== false);
    setLanguageState(lang);
  };
  const dict = useMemo<Dict>(() => resources[language] ?? resources.en, [language]);

  const t = (key: string, fallback?: string) => {
    // Same English-before-key fallback as tLang above, for body copy.
    const v = lookup(key, dict) ?? lookup(key, resources.en);
    if (v == null) return fallback ?? key;
    return typeof v === "string" ? v : String(v);
  };

  const value = useMemo(() => ({ language, setLanguage, t }), [language]);
  return <PortalLanguageContext.Provider value={value}>{children}</PortalLanguageContext.Provider>;
};

const fallbackCtx: PortalLanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: (k: string, fb?: string) => fb ?? k
};

export function usePortalLanguage() {
  const ctx = useContext(PortalLanguageContext);
  if (!ctx) {
    if (import.meta.env.DEV) throw new Error("usePortalLanguage must be used within PortalLanguageProvider");
    console.warn("[i18n] usePortalLanguage used outside provider. Using fallback.");
    return fallbackCtx;
  }
  return ctx;
}
